import type { OpencodeToolPart } from "schemas/generated/opencode";

import React from "react";

import {
  getContextualToolTitle,
  isSkillTool,
} from "@agent-ui/features/chat/utils/toolDisplay";
import { Button } from "@replo/design-system/components/button/Button";
import { Popover } from "@replo/design-system/components/popover/Popover";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { BotIcon } from "@replo/design-system/icons/animated/BotIcon";
import { CircleCheckIcon } from "@replo/design-system/icons/animated/CircleCheckIcon";
import { LinkIcon } from "@replo/design-system/icons/animated/LinkIcon";
import { SearchIcon } from "@replo/design-system/icons/animated/SearchIcon";
import { SquarePenIcon } from "@replo/design-system/icons/animated/SquarePenIcon";
import twMerge from "@replo/design-system/utils/twMerge";
import { AlertCircle, MoreHorizontal } from "lucide-react";
import { formatDuration } from "replo-utils/lib/datetime";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const ToolStatusIcon = ({ toolPart }: { toolPart: OpencodeToolPart }) => {
  const status = toolPart.state.status;
  const toolLowerName = toolPart.tool.toLowerCase();

  if (status === "completed") {
    return <CircleCheckIcon size={14} animateOnMount />;
  }
  if (status === "error") {
    return <AlertCircle size={14} className="text-red" />;
  }

  const isSearch = toolLowerName === "grep" || toolLowerName === "glob";
  if (isSearch) {
    return <SearchIcon size={14} loop />;
  }
  if (toolLowerName === "task") {
    return <BotIcon size={14} loop />;
  }
  if (toolLowerName.includes("web") || toolLowerName.includes("browser")) {
    return <LinkIcon size={14} loop />;
  }

  return <SquarePenIcon size={14} loop />;
};

export const ToolRow = ({ toolPart }: { toolPart: OpencodeToolPart }) => {
  const state = toolPart.state;
  const contextualTitle = getContextualToolTitle(toolPart.tool, state.input);
  const isSkill = isSkillTool(toolPart.tool);

  const title = exhaustiveSwitch(
    state,
    "status",
  )({
    pending: () => `${contextualTitle}...`,
    running: () => `${contextualTitle}...`,
    completed: () => {
      return isSkill
        ? `Loaded skill ${contextualTitle}`
        : `${contextualTitle} complete`;
    },
    error: () => `${contextualTitle} failed`,
  });

  const duration = exhaustiveSwitch(
    state,
    "status",
  )({
    pending: () => null,
    running: () => null,
    completed: (completedState) => {
      return formatDuration(
        completedState.time.end - completedState.time.start,
      );
    },
    error: (errorState) => {
      return formatDuration(errorState.time.end - errorState.time.start);
    },
  });

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between w-full min-h-8">
        <div className="flex items-center gap-2">
          <ToolStatusIcon toolPart={toolPart} />
          <span
            className={twMerge(
              "text-sm truncate min-w-0 text-default",
              state.status === "error" && "text-red",
            )}
          >
            {title}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {duration && (
            <span className="text-xs text-muted whitespace-nowrap">
              {duration}
            </span>
          )}
          <Popover
            side="right"
            align="start"
            sideOffset={8}
            UNSAFE_className="w-[520px]"
            title={`${contextualTitle} state`}
            trigger={
              <Tooltip content="View tool state">
                <Button icon variant="tertiary" size="sm">
                  <MoreHorizontal />
                </Button>
              </Tooltip>
            }
          >
            <div className="max-h-[520px] overflow-y-auto">
              <pre className="text-xs whitespace-pre-wrap break-all font-mono bg-surface p-3 rounded-md">
                {JSON.stringify(toolPart, null, 2)}
              </pre>
            </div>
          </Popover>
        </div>
      </div>
    </div>
  );
};
